import { Component, ElementRef, OnInit } from '@angular/core'
import { Router, ActivatedRoute } from '@angular/router'

import { AuthService, InsaService, LocalizeService } from '../../insa-services'
import { SharedMethodsModule } from '../../insa-custom-scripts'

// Final step of the logged-in "change password" flow:
//   Settings -> Change Password -> AuthService.requestPasswordChange()
//   -> twoFactorValidate / twoFactorCertificate (purpose 'change')
//   -> here, with a short-lived 'change' token (AuthService.getPasswordChangeToken()).
// Modelled on ResetPasswordComponent; the difference is that the user still
// knows (and must confirm) the current password.
@Component({
  templateUrl: 'changePasswordVerified.component.html'
})
export class ChangePasswordVerifiedComponent implements OnInit {
  currentPassword: string = "";
  newPassword: string = "";
  confirmPassword: string = "";
  submitted: boolean = false;
  twoFactorWasReset: boolean = false;

  constructor(
    private _authService: AuthService,
    private _insaService: InsaService,
    private _router: Router,
    private _route: ActivatedRoute,
    private _localize: LocalizeService,
    private _element: ElementRef,
  ) { }

  ngOnInit(): void {
    // Only reachable right after a successful 2FA/certificate verification
    // for a 'change' purpose - otherwise go back into the app.
    if (!this._authService.getPasswordChangeToken()) {
      this._router.navigate(['/']);
      return;
    }

    // Set by twoFactorValidate when a recovery code was used: that wipes the
    // 2FA enrollment, so the user will have to set it up again on next login.
    this.twoFactorWasReset = this._route.snapshot.queryParamMap.has('twoFactorReset');
  }

  get currentPasswordPlaceholder(): string {
    return this.res('Res402', 'Stara lozinka');
  }

  get newPasswordPlaceholder(): string {
    return this.res('Res403', 'Nova lozinka');
  }

  get confirmPasswordPlaceholder(): string {
    return this.res('Res1645', 'Potvrdite novu lozinku');
  }

  // Korisnik je ovdje prijavljen, pa su resursi (RESOURCES) dostupni;
  // loginTranslations (LOOKUP) nemaju sr-RS.
  private res(key: string, fallback: string): string {
    return this._localize.getLocalizedResource(key) || this._localize.getTranslation(fallback);
  }

  cancel(): void {
    this._authService.clearPasswordChangeToken();
    this._router.navigate(['/']);
  }

  onSubmit(form: any): void {
    if (form['newPassword'] !== form['confirmPassword']) {
      SharedMethodsModule.addNotification(
        "#notificationChangePasswordVerified",
        this.res('Res408', 'Lozinke nisu iste!'),
        false
      );
      return;
    }

    if (form['currentPassword'] === form['newPassword']) {
      SharedMethodsModule.addNotification(
        "#notificationChangePasswordVerified",
        this.res('Res406', 'Stara i nova lozinka su iste!'),
        false
      );
      return;
    }

    this.submitted = true;
    kendo.ui.progress($(".main-loading"), true);

    this._authService
      .changePasswordVerified(
        this._authService.getPasswordChangeToken(),
        form['currentPassword'],
        form['newPassword']
      )
      .subscribe({
        next: () => {
          kendo.ui.progress($(".main-loading"), false);
          this._authService.clearPasswordChangeToken();

          // Backend revoked every session of this user (incl. the current
          // one) - sign out locally the same way selectLanguage() in
          // settings does, and land on /login with a confirmation.
          this._authService.logout();
          this._insaService.clearData();

          this._router.navigate(['/login']).then(() => {
            SharedMethodsModule.addNotification(
              "#notification",
              this.res('Res1712', 'Lozinka je uspešno promenjena. Prijavite se novom lozinkom.'),
              true
            );
          });
        },
        error: (err) => {
          kendo.ui.progress($(".main-loading"), false);
          this.submitted = false;

          // Expired/invalid change token: the 2FA step has to be redone.
          if (err?.status === 401) {
            this._authService.clearPasswordChangeToken();
            this._router.navigate(['/']).then(() => {
              SharedMethodsModule.addNotification(
                "#notificationPasswordChange",
                this.res('Res1646', 'Verifikacija je istekla. Ponovo pokrenite promenu lozinke.'),
                false
              );
            });
            return;
          }

          // Wrong current password / weak new password: stay here and keep
          // the token so the user can retry without redoing 2FA (the backend
          // rate-limits the current-password check).
          SharedMethodsModule.addNotification(
            "#notificationChangePasswordVerified",
            err?.error?.message || this.res('Res1647', 'Promena lozinke nije uspela. Pokušajte ponovo.'),
            false
          );
        }
      });
  }

  ngAfterViewInit() {
    this._localize.applyTranslations(this._element.nativeElement);
  }
}



// WEBPACK FOOTER //
// ./app/insa-components/insa-login/changePasswordVerified.component.ts